// frontend/src/modules/Confirmations/index.tsx
import { useState, useEffect, useMemo } from 'react';
import {
  Card, Button, Stack, Group, Text, Table, Badge, ActionIcon,
  Tooltip, Loader, Center, ThemeIcon, Container, Select
} from '@mantine/core';
import {
  IconPlus, IconEye, IconTrash, IconFileCheck, IconAlertTriangle,
  IconSortAscending, IconSortDescending, IconX
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { useMediaQuery } from '@mantine/hooks';
import { confirmationsApi, Confirmation } from '@/api/confirmations.api';
import CreateConfirmationModal from './components/CreateConfirmationModal';
import dayjs from 'dayjs';

type SortField = 'created';
type SortDir = 'asc' | 'desc';

const Confirmations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [confirmations, setConfirmations] = useState<Confirmation[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalVisible, setCreateModalVisible] = useState(false);

  const [filterProperty, setFilterProperty] = useState<string | null>(null);
  const [filterName, setFilterName] = useState<string | null>(null);
  const [sortField] = useState<SortField>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  useEffect(() => { fetchConfirmations(); }, []);

  const fetchConfirmations = async () => {
    try {
      setLoading(true);
      const response = await confirmationsApi.getAll();
      setConfirmations(response.data.data || []);
    } catch {
      notifications.show({ title: t('errors.generic'), message: 'Failed to load', color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const propertyOptions = useMemo(() => {
    const names = [...new Set(confirmations.map(c => c.property_name).filter(Boolean))];
    return names.map(n => ({ value: n!, label: n! }));
  }, [confirmations]);

  const nameOptions = useMemo(() => {
    const names = [...new Set(confirmations.map(c => c.agent_name).filter(Boolean))];
    return names.map(n => ({ value: n!, label: n! }));
  }, [confirmations]);

  const displayData = useMemo(() => {
    let data = [...confirmations];
    if (filterProperty) data = data.filter(c => c.property_name === filterProperty);
    if (filterName) data = data.filter(c => c.agent_name === filterName);
    data.sort((a, b) => {
      const va = a.created_at || '';
      const vb = b.created_at || '';
      return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return data;
  }, [confirmations, filterProperty, filterName, sortField, sortDir]);

  const handleDelete = (id: number, number: string) => {
    modals.openConfirmModal({
      title: t('common.confirmDelete'),
      children: <Text size="sm">Delete <strong>{number}</strong>?</Text>,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try { await confirmationsApi.delete(id); fetchConfirmations(); }
        catch { notifications.show({ title: t('errors.generic'), message: 'Failed', color: 'red' }); }
      }
    });
  };

  const fmt = (n: number | undefined | null) => n ? n.toLocaleString() : '—';
  const isOverdue = (d: string | undefined | null) => d ? dayjs(d).isBefore(dayjs(), 'day') : false;

  return (
    <Container size="xl" p={isMobile ? 'sm' : 'md'}>
      <Stack gap="lg">
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" wrap="nowrap">
            <Group gap="sm">
              <ThemeIcon size="xl" radius="md" variant="gradient" gradient={{ from: 'teal', to: 'cyan' }}>
                <IconFileCheck size={28} />
              </ThemeIcon>
              <div>
                <Text fw={600} size="xl">Confirmations</Text>
                <Text size="xs" c="dimmed">{confirmations.length} documents</Text>
              </div>
            </Group>
            <Button variant="gradient" gradient={{ from: 'teal', to: 'cyan' }}
              leftSection={<IconPlus size={18} />} onClick={() => setCreateModalVisible(true)}>
              {t('common.create')}
            </Button>
          </Group>
        </Card>

        {/* Filters */}
        <Card shadow="sm" padding="sm" radius="md" withBorder>
          <Group gap="sm" wrap="wrap">
            <Select
              placeholder="Filter by property"
              data={propertyOptions}
              value={filterProperty}
              onChange={setFilterProperty}
              clearable searchable
              size="xs" w={200}
            />
            <Select
              placeholder="Filter by name"
              data={nameOptions}
              value={filterName}
              onChange={setFilterName}
              clearable searchable
              size="xs" w={180}
            />
            {(filterProperty || filterName) && (
              <ActionIcon variant="subtle" size="xs" onClick={() => { setFilterProperty(null); setFilterName(null); }}>
                <IconX size={12} />
              </ActionIcon>
            )}
            <div style={{ marginLeft: 'auto' }}>
              <Tooltip label={sortDir === 'desc' ? 'Newest first' : 'Oldest first'}>
                <ActionIcon variant="light" size="sm" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}>
                  {sortDir === 'desc' ? <IconSortDescending size={14} /> : <IconSortAscending size={14} />}
                </ActionIcon>
              </Tooltip>
            </div>
          </Group>
        </Card>

        <Card shadow="sm" padding="lg" radius="md" withBorder>
          {loading ? (
            <Center p="xl"><Loader size="lg" /></Center>
          ) : displayData.length === 0 ? (
            <Center p="xl">
              <Stack align="center" gap="md">
                <ThemeIcon size={60} radius="xl" variant="light" color="gray"><IconFileCheck size={30} /></ThemeIcon>
                <Text c="dimmed" size="sm">{(filterProperty || filterName) ? 'No confirmations match filters' : 'No confirmations yet'}</Text>
              </Stack>
            </Center>
          ) : (
            <Table.ScrollContainer minWidth={1100}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Number</Table.Th>
                    <Table.Th>Property</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Arrival</Table.Th>
                    <Table.Th>Departure</Table.Th>
                    <Table.Th ta="right">Other Fees</Table.Th>
                    <Table.Th ta="right">Room Fees</Table.Th>
                    <Table.Th ta="right">Total</Table.Th>
                    <Table.Th ta="right">Paid</Table.Th>
                    <Table.Th>Next Payment</Table.Th>
                    <Table.Th ta="right">{t('common.actions')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {displayData.map((c) => {
                    const otherFees = Number(c.total_expected) || 0;
                    const roomFees = Number(c.total_room_fees) || 0;
                    const combinedTotal = otherFees + roomFees;
                    const totalPaid = Number(c.total_paid) || 0;
                    const paidPercent = combinedTotal > 0 ? Math.round((totalPaid / combinedTotal) * 100) : 0;
                    const fullyPaid = combinedTotal > 0 && totalPaid >= combinedTotal;
                    const overduePayment = isOverdue(c.next_payment_date);

                    return (
                      <Table.Tr key={c.id} style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/confirmations/${c.id}`)}>
                        <Table.Td>
                          <Text size="xs" fw={500} lineClamp={1}>{c.confirmation_number}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap={6} wrap="nowrap">
                            {c.property_number && (
                              <Badge size="sm" variant="filled" color="blue" style={{ flexShrink: 0 }}>
                                {c.property_number}
                              </Badge>
                            )}
                            <Text size="sm" lineClamp={1} style={{ minWidth: 0 }}>{c.property_name || '—'}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={1}>{c.agent_name || '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{c.arrival_date ? dayjs(c.arrival_date).format('DD.MM.YY') : '—'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{c.departure_date ? dayjs(c.departure_date).format('DD.MM.YY') : '—'}</Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm">{otherFees > 0 ? fmt(otherFees) : '—'}</Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm" c="dimmed">{roomFees > 0 ? fmt(roomFees) : '—'}</Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          <Text size="sm" fw={600}>{combinedTotal > 0 ? fmt(combinedTotal) : '—'}</Text>
                        </Table.Td>
                        <Table.Td ta="right">
                          {combinedTotal > 0 ? (
                            <Group gap={4} justify="flex-end">
                              <Text size="sm" fw={500} c={fullyPaid ? 'green' : totalPaid > 0 ? 'orange' : 'dimmed'}>
                                {fmt(totalPaid)}
                              </Text>
                              <Badge size="xs" variant="light"
                                color={fullyPaid ? 'green' : totalPaid > 0 ? 'orange' : 'gray'}>
                                {paidPercent}%
                              </Badge>
                            </Group>
                          ) : totalPaid > 0 ? (
                            /* No expected amount configured, but a payment is recorded —
                               still surface it clearly as Paid so the list stays in sync
                               with the payment registered in the confirmation. */
                            <Group gap={4} justify="flex-end">
                              <Text size="sm" fw={500} c="green">{fmt(totalPaid)}</Text>
                              <Badge size="xs" variant="light" color="green">Paid</Badge>
                            </Group>
                          ) : (
                            <Text size="sm" c="dimmed">—</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {c.next_payment_date ? (
                            <Group gap={4}>
                              {overduePayment && <IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />}
                              <div>
                                <Text size="sm" fw={500} c={overduePayment ? 'red' : undefined}>
                                  {dayjs(c.next_payment_date).format('DD.MM.YY')}
                                </Text>
                                {c.next_payment_amount && (
                                  <Text size="xs" c="dimmed">{fmt(c.next_payment_amount)}</Text>
                                )}
                              </div>
                            </Group>
                          ) : (
                            <Text size="sm" c="dimmed">—</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs" justify="flex-end" onClick={(e) => e.stopPropagation()}>
                            <Tooltip label={t('common.view')}>
                              <ActionIcon variant="light" color="blue" onClick={() => navigate(`/confirmations/${c.id}`)}>
                                <IconEye size={16} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t('common.delete')}>
                              <ActionIcon variant="light" color="red" onClick={() => handleDelete(c.id, c.confirmation_number)}>
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Card>
      </Stack>

      <CreateConfirmationModal
        visible={createModalVisible}
        onCancel={() => { setCreateModalVisible(false); fetchConfirmations(); }}
        onSuccess={() => { setCreateModalVisible(false); fetchConfirmations(); }}
        onCreated={() => { fetchConfirmations(); }}
      />
    </Container>
  );
};

export default Confirmations;
